import {
  Component,
  ContentChildren,
  QueryList,
  Input,
  Output,
  EventEmitter,
  AfterContentInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, NgFor, NgIf, DatePipe } from '@angular/common';
import { TableCellDef } from '../../directives/table-cell.directive';

export type TableColumn<T = any> = {
  header: string; // Texto de cabecera
  field?: keyof T | string; // Propiedad a mostrar (p.ej. 'email' o 'user.name')
  cell?: string; // Nombre del ng-template (para celdas custom)
  format?: (value: any, row: T) => any; // Formateador opcional
  widthClass?: string; // Tailwind opcional por columna
};

@Component({
  selector: 'app-data-table',
  imports: [CommonModule, NgIf, NgFor],
  templateUrl: './data-table.html',
  styleUrl: './data-table.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTable<T = any> implements AfterContentInit, OnDestroy {
  @Input({ required: true }) columns: TableColumn<T>[] = [];
  @Input() rows: T[] = [];
  @Input() loading = false;

  // búsqueda
  @Input() showSearch = true;
  @Input() searchTerm = '';
  @Input() searchPlaceholder = 'Buscar...';
  @Output() search = new EventEmitter<string>();

  // paginación
  @Input() showPagination = true;
  @Input() page = 1;
  @Input() pageSize = 20;
  @Input() totalCount = 0;
  @Output() pageChange = new EventEmitter<number>();

  // contador
  @Input() showCounter = true;

  @Input() height: string | number = '83vh';
  @Input() stickyHeader = true;
  @Input() striped = true;

  // trackBy opcional
  @Input() trackBy: (index: number, row: T) => any = (_, r: any) => r?.id ?? _;

  @ContentChildren(TableCellDef) cellDefs!: QueryList<TableCellDef>;
  cellTemplates: Record<string, any> = {};
  private sub?: any;

  ngAfterContentInit(): void {
    const build = () => {
      this.cellTemplates = {};
      this.cellDefs?.forEach(
        (def) => (this.cellTemplates[def.name] = def.template),
      );
    };
    build();
    this.sub = this.cellDefs?.changes?.subscribe(build);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe?.();
  }

  // helpers
  onSearch(v: string) {
    this.search.emit(v);
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil((this.totalCount || 0) / (this.pageSize || 1)),
    );
  }

  get isSinglePage(): boolean {
    return this.totalPages <= 1;
  }

  get pagesArray(): number[] {
    return Array(this.totalPages)
      .fill(0)
      .map((_, i) => i + 1);
  }
  get startItem(): number {
    return this.totalCount ? (this.page - 1) * this.pageSize + 1 : 0;
  }
  get endItem(): number {
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  get computedHeight(): string {
    return typeof this.height === 'number'
      ? `${this.height}px`
      : this.height || '100vh';
  }

  prev() {
    if (this.page > 1) this.pageChange.emit(this.page - 1);
  }
  next() {
    if (this.page < this.totalPages) this.pageChange.emit(this.page + 1);
  }
  goTo(n: number) {
    if (n >= 1 && n <= this.totalPages) this.pageChange.emit(n);
  }

  // obtiene valor por field (soporta path simple "a.b.c")
  getValue(row: any, col: TableColumn<T>) {
    const value = col.field ? this.resolvePath(row, String(col.field)) : row;
    return col.format ? col.format(value, row) : value;
  }

  private resolvePath(obj: any, path: string) {
    return path
      .split('.')
      .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  }
}
